import {useEffect, useSyncExternalStore} from "react";
import api from "../../system/api";
import {getStorage} from "../../system/storage";

// The workspace's subscription status, decided on the server (zofino-strapi src/billing/subscription-status.ts;
// docs/tasks/36-subscription-status.md). One request per workspace shared by the sidebar indicator and the banner;
// a workspace switch never shows the previous workspace's state; refreshed on focus (at most every 5 minutes) and right
// after a payment / renewal / plan change (refreshSubscriptionStatus).
const STALE_MS = 5 * 60 * 1000;
let snapshot = {workspace: null, status: null, loading: false, error: false, loadedAt: 0};
const listeners = new Set();
let inflight = null;

const emit = (next) => {
    snapshot = {...snapshot, ...next};
    listeners.forEach((listener) => listener());
};

export async function refreshSubscriptionStatus(workspace = getStorage("workspace")) {
    if (!workspace) return null;
    if (inflight && snapshot.workspace === workspace) return inflight;
    // another workspace: drop the old state at once
    if (snapshot.workspace !== workspace) emit({workspace, status: null, error: false, loadedAt: 0});
    emit({loading: true});
    inflight = api.get("/workspace-subscription-status", {headers: {workspace}, suppressGlobalErrorHandler: true})
        .then((response) => {
            if (snapshot.workspace !== workspace) return null;
            emit({status: response?.data?.data || null, error: false, loading: false, loadedAt: Date.now()});
            return snapshot.status;
        })
        .catch(() => {
            // an error is an error — never shown as «expired» or «no subscription»
            if (snapshot.workspace === workspace) emit({error: true, loading: false, loadedAt: Date.now()});
            return null;
        })
        .finally(() => { inflight = null; });
    return inflight;
}

export function useSubscriptionStatus(workspace) {
    const state = useSyncExternalStore((listener) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }, () => snapshot);
    useEffect(() => {
        if (!workspace) return undefined;
        if (snapshot.workspace !== workspace || !snapshot.loadedAt) refreshSubscriptionStatus(workspace);
        const onFocus = () => {
            if (snapshot.workspace === workspace && Date.now() - snapshot.loadedAt > STALE_MS) refreshSubscriptionStatus(workspace);
        };
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [workspace]);
    return state.workspace === workspace ? state : {workspace, status: null, loading: true, error: false};
}

// tests only
export function resetSubscriptionStatusStore() {
    snapshot = {workspace: null, status: null, loading: false, error: false, loadedAt: 0};
    inflight = null;
}
